import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, User, MessageSquare, CheckCircle, AlertCircle, FileText } from 'lucide-react';
import emailjs from '@emailjs/browser';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  emailTo?: string;
  // EmailJS configuration - get these from https://www.emailjs.com/
  emailjsServiceId?: string;
  emailjsAutoReplyTemplateId?: string; // Template for auto-reply to user
  emailjsNotificationTemplateId?: string; // Template for notification to you
  emailjsPublicKey?: string;
}

export const ContactModal: React.FC<ContactModalProps> = ({ 
  isOpen, 
  onClose, 
  emailTo = 'williamwildernash@gmail.com',
  emailjsServiceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || '',
  emailjsAutoReplyTemplateId = process.env.REACT_APP_EMAILJS_AUTO_REPLY_TEMPLATE_ID || '',
  emailjsNotificationTemplateId = process.env.REACT_APP_EMAILJS_NOTIFICATION_TEMPLATE_ID || '',
  emailjsPublicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || ''
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    title: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Check if EmailJS is configured
    if (!emailjsServiceId || !emailjsAutoReplyTemplateId || !emailjsNotificationTemplateId || !emailjsPublicKey) {
      // Fallback to mailto if EmailJS not configured
      const subject = formData.title || `Contact from ${formData.name}`;
      const body = `Name: ${formData.name}%0D%0AEmail: ${formData.email}%0D%0ASubject: ${formData.title}%0D%0A%0D%0AMessage:%0D%0A${formData.message}`;
      const mailtoLink = `mailto:${emailTo}?subject=${encodeURIComponent(subject)}&body=${body}`;
      window.location.href = mailtoLink;
      setIsSubmitting(false);
      setSubmitted(true);
      setTimeout(() => {
        setFormData({ name: '', email: '', title: '', message: '' });
        setSubmitted(false);
        onClose();
      }, 2000);
      return;
    }

    try {
      // 1. Send auto-reply email to the user
      // Template should have:
      // - To Email: {{user_email}} (or set dynamically in template)
      // - Variables: {{name}} and {{title}}
      const autoReplyParams = {
        name: formData.name,
        title: formData.title,
        user_email: formData.email, // For dynamic recipient if template supports it
      };

      await emailjs.send(
        emailjsServiceId,
        emailjsAutoReplyTemplateId,
        autoReplyParams,
        emailjsPublicKey
      );

      // 2. Send notification email to you
      // Template should have:
      // - To Email: williamwildernash@gmail.com (set in template settings)
      // - Variables: {{from_name}}, {{from_email}}, {{title}}, {{message}}
      const notificationParams = {
        from_name: formData.name,
        from_email: formData.email,
        title: formData.title,
        message: formData.message,
        reply_to: formData.email, // This allows you to reply directly to the sender
      };

      await emailjs.send(
        emailjsServiceId,
        emailjsNotificationTemplateId,
        notificationParams,
        emailjsPublicKey
      );

      // Success!
      setIsSubmitting(false);
      setSubmitted(true);
      setTimeout(() => {
        setFormData({ name: '', email: '', title: '', message: '' });
        setSubmitted(false);
        onClose();
      }, 3000);
    } catch (err: any) {
      setIsSubmitting(false);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to send message. Please try again or email directly.';
      if (err?.text) {
        if (err.text.includes('recipients address is empty')) {
          errorMessage = 'EmailJS template not configured correctly. Please set the recipient email in your EmailJS template settings.';
        } else {
          errorMessage = `Error: ${err.text}`;
        }
      }
      setError(errorMessage);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-background/80 z-40"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="fixed right-0 top-0 h-full w-full sm:w-[500px] bg-background/95 border-l border-border z-50 shadow-2xl max-w-full will-change-transform"
            style={{ touchAction: 'pan-y', maxWidth: '100vw' }}
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-border">
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-border border border-border flex items-center justify-center flex-shrink-0">
                    <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-foreground" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg sm:text-xl font-medium text-foreground geist-font truncate">Get In Touch</h2>
                    <p className="text-xs sm:text-sm text-muted-foreground inter-font">Send me a message</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg glass-button flex items-center justify-center hover:bg-border/50 active:bg-border/70 transition-colors flex-shrink-0 ml-2"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-foreground" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                {!submitted ? (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Name Field */}
                    <div className="space-y-2">
                      <label htmlFor="name" className="text-xs sm:text-sm font-medium text-foreground inter-font flex items-center gap-2">
                        <User className="h-3 w-3 sm:h-4 sm:w-4" />
                        Your Name
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg glass-card border border-border bg-background/50 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-foreground/20 inter-font text-sm sm:text-base"
                      />
                    </div>

                    {/* Email Field */}
                    <div className="space-y-2">
                      <label htmlFor="email" className="text-xs sm:text-sm font-medium text-foreground inter-font flex items-center gap-2">
                        <Mail className="h-3 w-3 sm:h-4 sm:w-4" />
                        Your Email
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg glass-card border border-border bg-background/50 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-foreground/20 inter-font text-sm sm:text-base"
                      />
                    </div>

                    {/* Title Field */}
                    <div className="space-y-2">
                      <label htmlFor="title" className="text-xs sm:text-sm font-medium text-foreground inter-font flex items-center gap-2">
                        <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
                        Subject
                      </label>
                      <input
                        id="title"
                        name="title"
                        type="text"
                        required
                        value={formData.title}
                        onChange={handleChange}
                        placeholder="What is this regarding?"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg glass-card border border-border bg-background/50 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-foreground/20 inter-font text-sm sm:text-base"
                      />
                    </div>

                    {/* Message Field */}
                    <div className="space-y-2">
                      <label htmlFor="message" className="text-xs sm:text-sm font-medium text-foreground inter-font flex items-center gap-2">
                        <MessageSquare className="h-3 w-3 sm:h-4 sm:w-4" />
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell me about your project..."
                        rows={5}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg glass-card border border-border bg-background/50 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-foreground/20 inter-font resize-none text-sm sm:text-base"
                      />
                    </div>

                    {/* Error Message */}
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive"
                      >
                        <AlertCircle className="h-4 w-4 flex-shrink-0" />
                        <p className="text-sm inter-font">{error}</p>
                      </motion.div>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full primary-button px-4 sm:px-6 py-3 sm:py-4 text-foreground rounded-lg font-medium text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </button>

                    {/* Email Info */}
                    <p className="text-xs text-muted-foreground text-center inter-font">
                      Or email directly at{' '}
                      <a href={`mailto:${emailTo}`} className="text-foreground hover:underline">
                        {emailTo}
                      </a>
                    </p>
                  </form>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center justify-center h-full text-center space-y-4"
                  >
                    <div className="w-16 h-16 rounded-full bg-border/50 flex items-center justify-center">
                      <CheckCircle className="h-8 w-8 text-foreground" />
                    </div>
                    <div>
                      <h3 className="text-xl font-medium text-foreground geist-font mb-2">Message Sent!</h3>
                      <p className="text-muted-foreground inter-font">I'll get back to you soon.</p>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

